import React from 'react';

interface Props {
  children: React.ReactNode;
  /** Optional label so we know which boundary tripped (route vs app shell). */
  scope?: string;
}

interface State {
  error: Error | null;
}

/**
 * Top-level + per-route safety net. A render/lifecycle throw anywhere below
 * this boundary is caught here instead of unmounting the whole React tree to a
 * blank white screen — critical for a live trading dashboard.
 *
 * `reportError` is the single hook for wiring an external monitor (Sentry,
 * etc.) later; today it logs to the console so prod crashes are at least
 * visible in the browser, and POSTs a lightweight beacon if an endpoint exists.
 */
export class ErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    reportError(error, { scope: this.props.scope, componentStack: info.componentStack });
  }

  handleReset = () => {
    this.setState({ error: null });
  };

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <div
        role="alert"
        style={{
          minHeight: '60vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 16,
          padding: 24,
          textAlign: 'center',
          color: 'var(--t1, #0a1230)',
        }}
      >
        <div style={{ fontSize: 40 }}>⚠️</div>
        <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>Something went wrong</h2>
        <p style={{ margin: 0, maxWidth: 420, color: 'var(--t2, #5b6485)', fontSize: 14 }}>
          The page hit an unexpected error. Your data is safe. Try reloading — if it keeps
          happening, contact support.
        </p>
        <div style={{ display: 'flex', gap: 10 }}>
          <button
            onClick={this.handleReset}
            className="btn-ghost"
            style={{ padding: '8px 16px', borderRadius: 'var(--rad, 9px)', cursor: 'pointer' }}
          >
            Try again
          </button>
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: '8px 16px',
              borderRadius: 'var(--rad, 9px)',
              border: 'none',
              cursor: 'pointer',
              background: 'var(--primary, #635bff)',
              color: '#fff',
              fontWeight: 600,
            }}
          >
            Reload
          </button>
        </div>
      </div>
    );
  }
}

interface ErrorContext {
  scope?: string;
  componentStack?: string | null;
}

export function reportError(error: Error, ctx: ErrorContext = {}) {
  // Always log so the crash is visible somewhere.
  console.error(`[ErrorBoundary${ctx.scope ? `:${ctx.scope}` : ''}]`, error, ctx.componentStack);

  // Best-effort beacon. No-op unless VITE_ERROR_BEACON_URL is configured, so
  // this is safe to ship before any monitoring backend exists.
  const url = import.meta.env.VITE_ERROR_BEACON_URL;
  if (url && typeof navigator !== 'undefined' && navigator.sendBeacon) {
    try {
      navigator.sendBeacon(
        url,
        JSON.stringify({
          message: error.message,
          stack: error.stack,
          scope: ctx.scope,
          url: window.location.href,
          ts: Date.now(),
        }),
      );
    } catch {
      // never let reporting throw
    }
  }
}
